import React, {memo, useCallback, useState} from "react";
import {FormProvider, SubmitHandler, useForm} from "react-hook-form";
import * as yup from "yup";
import {yupResolver} from "@hookform/resolvers/yup";
import Toolbar from "@mui/material/Toolbar";
import {alpha, Collapse, SxProps, Theme} from "@mui/material";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import {useTheme} from "@mui/material/styles";
import {IconTextFieldSmallStyled} from "../form/FormField";
import ExportButton, {ExportButtonProps} from "../form/ExportButton";
import Icon from "../ui/Icon";
import ScrollBar from "../ui/ScrollBar";
import CustomButton from "../ui/CustomButton";
import StyledBadge from "../ui/StyledBadge";
import Grid from "@mui/material/Grid";

interface ToolbarActionButtonProps {
    onClick: (event: React.MouseEvent<HTMLElement>) => void;
    activeFiltersNumber?: number;
    isActive?: boolean;
}

const clearButtonSx: SxProps<Theme> = {
    color: "error.main",
    backgroundColor: "transparent",
    "&:hover": {
        color: "secondary.main",
        backgroundColor: "transparent",
    },
    "&[aria-expanded='true']": {
        backgroundColor: "white",
    },
    "&.Mui-disabled": {
        border: "none",
        color: "text.disabled",
    },
};

const useIsMdUp = () => {
    const theme = useTheme();
    return useMediaQuery(theme.breakpoints.up("md"));
};

const FilterButton = memo(({onClick, activeFiltersNumber}: ToolbarActionButtonProps) => {
    const isMdUp = useIsMdUp();

    return (
        <CustomButton
            buttonType="textIcon"
            bgcolor="toolbar"
            sxExtra={{
                marginLeft: 1.5,
            }}
            onClick={onClick}
            icon={
                <StyledBadge badgeContent={activeFiltersNumber}>
                    <Icon icon="fas bars-filter" fixedWidth={true} sx={{fontSize: "1rem"}} />
                </StyledBadge>
            }
        >
            {isMdUp ? "Filtrează" : ""}
        </CustomButton>
    );
});

const SearchButton = memo(({onClick, isActive} : ToolbarActionButtonProps) => {
    const isMdUp = useIsMdUp();

    if (isActive) {
        return null;
    }

    return (
        <CustomButton
            buttonType="textIcon"
            bgcolor="toolbar"
            onClick={onClick}
            icon={<Icon
                icon="fas magnifying-glass"
                fixedWidth={true}
                sx={{
                    display: "inline-flex",
                    marginBottom: "2px",
                }}
                style={{
                    width: "1rem",
                    height: "1rem",
                }}
            />}
        >
            {isMdUp ? "Caută" : ""}
        </CustomButton>
    );
});

interface SearchFormProps {
    onSubmit: (data: any) => void;
    query: string;
    onClear: () => void;
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

export interface FormInputsProps {
    keywords: string;
}

const SearchForm = ({onSubmit, onChange, query, onClear}: SearchFormProps) => {

    return (
        <Box
            display="flex"
            alignItems="center"
            width={{xs: "200px", sm: "250px"}}
        >
            <IconTextFieldSmallStyled
                name="keywords"
                size="small"
                fullWidth
                margin="normal"
                label="Caută..."
                autoComplete="off"
                autoFocus={true}
                type="text"
                sx={{
                    backgroundColor: "#BDCFF3",
                    "& .MuiOutlinedInput-root": {
                        backgroundColor: "#BDCFF3",
                        borderRadius: 0,
                        "& fieldset": {
                            borderRadius: 0,
                        },
                    },
                }}
                value={query}
                onChange={onChange}
            />

            <CustomButton
                onClick={() => onSubmit(query)}
                bgcolor="toolbar"
                buttonType="icon"
                width={40}
                height={40}
                icon={<Icon icon="fas magnifying-glass" fixedWidth={true} sx={{fontSize: "1rem"}}/>}
            />

            <CustomButton
                buttonType="icon"
                appearance="circle"
                bgcolor="white"
                width={40}
                height={40}
                onClick={onClear}
                icon={<Icon icon="far xmark" fixedWidth={true} sx={{fontSize: "1.2rem"}}/>}
                sxExtra={clearButtonSx}
            />
        </Box>
    );
};

interface EnhancedTableToolbarProps {
    title?: string;
    hasExport?: boolean;
    hasSelect?: boolean;
    selectOptions?: string | JSX.Element;
    exportData?: ExportButtonProps;
    hasFilters?: boolean;
    onOpenFilter?: (event: React.MouseEvent<HTMLElement>) => void;
    numSelected: number;
    activeFiltersNumber: number;
    hasSearch?: boolean;
    onSearch?: (value: string) => void;
    onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
    searchValue?: string;
    onClearSearch?: () => void;
}

const EnhancedTableToolbar = ({
    title = "",
    hasExport = false,
    hasSelect = false,
    selectOptions = "",
    exportData,
    hasFilters = false,
    onOpenFilter,
    numSelected,
    activeFiltersNumber,
    hasSearch = false,
    onSearch,
    onChange,
    searchValue,
    onClearSearch,
}: EnhancedTableToolbarProps) => {
    const [openSearch, setOpenSearch] = useState(false);

    const handleOpenFilter = useCallback((event: React.MouseEvent<HTMLElement>) => {
        onOpenFilter?.(event);
    }, [onOpenFilter]);

    const handleOpenSearch = useCallback(() => {
        setOpenSearch(true);
    }, []);

    const handleClearSearch = useCallback(() => {
        setOpenSearch(false);
        onClearSearch?.();
    }, [onClearSearch]);

    const handleSearchSubmit = useCallback((value: string) => {
        onSearch?.(value);
    }, [onSearch]);

    return (
        <Toolbar
            sx={{
                mb: 1,
                backgroundColor: (theme) =>
                    alpha(theme.palette.primary.main, theme.palette.action.activatedOpacity),
            }}
        >
            <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
                {(title || hasSelect) && (
                    <Grid item xs={12} sm={6} height="66px">
                        <Box sx={{display: 'flex', alignItems: "center", height: "56px"}}>
                            {title && (
                                <Typography
                                    variant="h6"
                                    id="tableTitle"
                                    component="div"
                                    noWrap
                                    sx={{
                                        minWidth: 0,
                                        fontWeight: 600,
                                    }}
                                >
                                    Titlu
                                </Typography>
                            )}

                            {hasSelect && (
                                <Typography variant="body2" fontWeight={600} sx={{
                                    whiteSpace: "nowrap",
                                }}>
                                    {numSelected === 1
                                        ? `${numSelected} selectat`
                                        : numSelected > 1
                                            ? `${numSelected} selectate`
                                            : "Nimic selectat"}
                                </Typography>
                            )}

                            {hasSelect && selectOptions !== "" && (
                                <>{selectOptions}</>
                            )}
                        </Box>
                    </Grid>
                )}

                <Grid item xs={12} sm={(title || hasSelect) ? 6 : 12} height="66px" alignItems="center">
                    <ScrollBar forceVisible="x" autoHide={false} style={{maxWidth: "100%"}}>
                        <Box
                            sx={{
                                width: "100%",
                                height: "56px",
                                display: "flex",
                                justifyContent: {xs: "flex-start", sm: "flex-end"},
                                alignItems: "center",
                            }}
                        >
                            {hasSearch && (
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        flexShrink: 0,
                                        border: openSearch ? "1px solid #fff" : "1px solid transparent",
                                        borderRadius: 1,
                                        overflow: "hidden",
                                        transition: "border-color 200ms ease",
                                    }}
                                >
                                    <Collapse
                                        in={openSearch}
                                        orientation="horizontal"
                                        timeout={250}
                                        unmountOnExit
                                    >
                                        <Box
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                whiteSpace: "nowrap",
                                                transform: openSearch ? "translateX(0)" : "translateX(16px)",
                                                opacity: openSearch ? 1 : 0,
                                                transition: "transform 250ms ease, opacity 200ms ease",
                                            }}
                                        >
                                            <SearchForm
                                                onSubmit={handleSearchSubmit}
                                                onClear={handleClearSearch}
                                                onChange={(event) => onChange?.(event as any)}
                                                query={searchValue ?? ""}
                                            />
                                        </Box>
                                    </Collapse>
                                    <SearchButton onClick={handleOpenSearch} isActive={openSearch} />
                                </Box>
                            )}

                            {hasFilters && (
                                <FilterButton
                                    onClick={handleOpenFilter}
                                    activeFiltersNumber={activeFiltersNumber}
                                />
                            )}

                            {hasExport && exportData && (
                                <ExportButton {...exportData} />
                            )}
                        </Box>
                    </ScrollBar>
                </Grid>
            </Grid>
        </Toolbar>
    );
};

export default EnhancedTableToolbar;